// What a node of the 3D scene is (docs/claude/rendering.md, *A scene node's
// record*): ONE record per node, set by `buildScene` where the node is added and
// read by every consumer — the picks, the shadows, the edges, a section's fill,
// Hide ceilings, Lights on, a partial rebuild and the exports. A WeakMap, never
// `userData`: the glTF exporter writes a node's userData into the file.

import * as THREE from 'three';
import type { SecPart } from '../section';

/** What a node is to the views. */
export type NodeRole =
  | 'fileRig'
  | 'glass'
  | 'glow'
  | 'group'
  | 'light'
  | 'line'
  | 'solid'
  | 'underlay'
  | 'viewRig'
  | 'water';

/** Each role's rules: `opaque` — a click stops on it (else it passes, unless its
 *  room's features are live), a section fills it where it closes a solid;
 *  `shadow` — it casts one (every mesh receives); `edges` — it writes depth, so
 *  the edge pass sees it. */
export const ROLE: Record<NodeRole, { opaque: boolean; shadow: boolean; edges: boolean }> = {
  fileRig: { opaque: false, shadow: false, edges: false },
  glass: { opaque: false, shadow: false, edges: true },
  glow: { opaque: true, shadow: true, edges: true },
  group: { opaque: false, shadow: false, edges: false },
  light: { opaque: false, shadow: false, edges: false },
  line: { opaque: true, shadow: false, edges: false },
  solid: { opaque: true, shadow: true, edges: true },
  underlay: { opaque: false, shadow: false, edges: true },
  viewRig: { opaque: false, shadow: false, edges: false },
  water: { opaque: false, shadow: false, edges: false },
};

/** A solid built as several meshes — a wall dressed in one mesh per finish, a
 *  shaped ceiling's shell (its underside and its roof) — with the band that
 *  closes it where it is built open (`seat`: a shell's, over the walls' tops). The
 *  seat is in no scene: a 3D section's fill draws it where it cuts the solid, and
 *  the viewer frees it with its meshes. */
export interface Solid { seat?: THREE.BufferGeometry }

export interface SceneNode {
  /** The drawing object it shows; null for an imported plan's underlay. */
  owner: string | null;
  role: NodeRole;
  /** The colour the exports write it in (OBJ, 3D DXF): its material's own, never
   *  the white a texture is multiplied by. */
  color: THREE.Color;
  /** Which of its room's or drawn wall's features it is (a section's source);
   *  none: its object's own. */
  part?: SecPart;
  /** The solid it is a piece of; none: a solid of its own. */
  solid?: Solid;
  /** A room's ceiling (a flat slab, a shaped shell): Hide ceilings hides it. */
  ceiling?: boolean;
}

const nodes = new WeakMap<THREE.Object3D, SceneNode>();

/** A node's record; none for what the build did not add (a ghost, a box). */
export function nodeOf(o: THREE.Object3D): SceneNode | undefined {
  return nodes.get(o);
}

/** Record what a node is. */
export function setNode(o: THREE.Object3D, n: SceneNode): void {
  nodes.set(o, n);
}

/** What a material makes of a mesh, set by the factory that makes it. */
const looks = new WeakMap<THREE.Material, { role: NodeRole; color: THREE.Color }>();

/** Register what a material makes of a mesh: its role and its export colour. */
export function withLook<M extends THREE.Material>(mat: M, role: NodeRole, color: THREE.ColorRepresentation): M {
  looks.set(mat, { role, color: new THREE.Color(color) });
  return mat;
}

/** A mesh's or a line's role and colour, by its material's look — a solid in the
 *  material's colour where none was registered. */
export function lookOf(o: THREE.Object3D): { role: NodeRole; color: THREE.Color } {
  const m = (o as THREE.Mesh).material;
  const mat = (Array.isArray(m) ? m[0] : m) as (THREE.Material & { color?: THREE.Color }) | undefined;
  const got = mat && looks.get(mat);
  if (got) return { role: got.role, color: got.color.clone() };
  return { role: (o as THREE.Line).isLine ? 'line' : 'solid', color: mat?.color?.clone() ?? new THREE.Color('#cccccc') };
}

/** What lies at floor level, and how high (mm): the studio's ground and its grid
 *  under the scene's lowest point, a room's lights-on floor (0 up to this), an
 *  imported plan lying on it (drawn over what is flush with it: a polygon offset). */
export const FLOOR_Z = { ground: -2, grid: -1, nightFloor: 1, underlay: 1 } as const;
